import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, DollarSign, ShieldCheck, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COVER = "https://images.unsplash.com/photo-1621177555630-b861919c864f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(email, password, name);
        toast.success("Account created");
      }
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0c0d10]">
      {/* Left cover */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-[#2b2f3d]">
        <img src={COVER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0c0d10] via-[#0c0d10]/70 to-transparent" />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-amber-500 live-dot" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.22em] text-amber-500/90 font-semibold">
            TimeTrak
          </span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-5xl xl:text-6xl font-black uppercase tracking-tight leading-none text-[#f3f4f8]">
            Every hour<br />after hours,<br /><span className="text-amber-500">accounted for.</span>
          </h1>
          <p className="mt-6 text-[#9fa6b9] max-w-md leading-relaxed">
            Log late shifts, auto-calculate duration and payout, and watch your weekly and monthly overtime totals add up.
          </p>
          <div className="mt-8 flex flex-wrap gap-5 font-mono-data text-xs text-[#62697e]">
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /> Auto duration</span>
            <span className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-500" /> Live payout</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#9fa6b9]" /> Private account</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-2 h-2 rounded-full bg-amber-500 live-dot" />
            <span className="font-mono-data text-[11px] uppercase tracking-[0.22em] text-amber-500/90 font-semibold">
              TimeTrak
            </span>
          </div>
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-[#f3f4f8]">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h2>
          <p className="mt-2 text-sm text-[#9fa6b9]">
            {mode === "login" ? "Access your overtime ledger." : "Start tracking your overtime in seconds."}
          </p>

          {mode === "register" && (
            <div className="mt-6">
              <label className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">Name</label>
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 w-full bg-[#14161c] border border-[#2b2f3d] rounded-lg px-3.5 py-2.5 text-[#f3f4f8] outline-none focus:border-amber-500 transition-colors"
                placeholder="Jane Doe"
              />
            </div>
          )}

          <div className="mt-5">
            <label className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full bg-[#14161c] border border-[#2b2f3d] rounded-lg px-3.5 py-2.5 text-[#f3f4f8] outline-none focus:border-amber-500 transition-colors"
              placeholder="you@work.com"
            />
          </div>

          <div className="mt-5">
            <label className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">Password</label>
            <input
              data-testid="auth-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full bg-[#14161c] border border-[#2b2f3d] rounded-lg px-3.5 py-2.5 text-[#f3f4f8] outline-none focus:border-amber-500 transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="auth-error" className="mt-4 text-sm text-red-400 font-mono-data">{error}</p>
          )}

          <button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading}
            className="mt-7 w-full group bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0c0d10] font-display font-bold uppercase tracking-wide rounded-lg px-4 py-3 flex items-center justify-center gap-2 transition-colors active:scale-[0.99]"
          >
            {loading ? "Working..." : mode === "login" ? "Sign In" : "Create Account"}
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>

          <button
            data-testid="auth-switch-mode-button"
            type="button"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-5 w-full text-center text-sm text-[#9fa6b9] hover:text-amber-500 transition-colors"
          >
            {mode === "login" ? "No account yet? Sign up" : "Already have an account? Sign in"}
          </button>

          {mode === "login" && (
            <p className="mt-6 text-center font-mono-data text-[11px] text-[#62697e]">
              Demo: admin@example.com / admin123
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
